/*
 * Experiment 041 - Product Gallery (PostHog flag: 041-product-gallery)
 *
 * control: the theme gallery is untouched.
 * test:    mobile  - Instagram-style carousel: full-bleed swipe, sliding dots, "1/N" counter, previous/next arrows.
 *          desktop - two-column grid of every image. The product-info column keeps its theme width but moves to the
 *                    right edge of the screen, and the gallery fills the space from the left edge to it. Both columns
 *                    use two-way sticky positioning so neither side shows empty space while the other scrolls.
 *
 * The theme gallery stays in the DOM (hidden) and remains the source of truth. Its frame-image logic
 * (theme-print.min.js / theme.min.js) swaps the second slide for the selected frame's version of that scene,
 * using media alt text such as "FieryBlooms-Scene2-Black", and jumps to it. This gallery mirrors the slide list,
 * updates only the changed image, and follows that jump after a visitor changes the variant. Zoom is delegated
 * to the theme's fancybox slides.
 * Preview: ?041-product-gallery=test or ?041-product-gallery=control
 * Diagnostics: every product page logs one "[041-product-gallery]" console line with the variant, its source and
 * PostHog's reason; add ?w99-debug=1 for the experiment library's step-by-step log.
 */
(function () {
  'use strict';

  var FLAG = '041-product-gallery';
  var TEST_CLASS = 'w99-041-product-gallery-test';
  var MOBILE_QUERY = '(max-width: 768px)';
  var DOTS_WINDOW = 5;
  var DOT_STEP = 10;
  var STICKY_GAP = 20;
  var EDGE_GUTTER = 8;
  var INFO_EDGE_GUTTER = 40;
  var VARIANT_CHANGE_WINDOW = 2000;
  var THEME_READY_TIMEOUT = 1500;
  var FRAME_VALUES = ['black', 'blackframe', 'white', 'whiteframe', 'gold', 'goldframe', 'wood', 'woodframe',
    'lightwood', 'lightwoodframe', 'darkwood', 'darkwoodframe'];
  var IMAGE_WIDTHS = [360, 540, 720, 900, 1080, 1296, 1600, 2048];
  var CHEVRON = '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var IS_FORCED = new URLSearchParams(window.location.search).has(FLAG);

  var DEBUG = /[?&]w99-debug(=|&|$)/.test(window.location.search);

  // One console line per page view so testers can see why a variant was shown.
  function logAssignment(variant) {
    try {
      var info = {
        variant: variant || 'none (not eligible: fewer than 2 gallery images)',
        source: IS_FORCED ? 'URL parameter (PostHog not called)' : 'PostHog'
      };
      var ph = window.posthog;
      if (!IS_FORCED && variant && ph && ph.__loaded) {
        info.flagValue = ph.getFeatureFlag(FLAG, { send_event: false });
        var details = ph.featureFlags && typeof ph.featureFlags.getFeatureFlagDetails === 'function'
          ? ph.featureFlags.getFeatureFlagDetails(FLAG) : null;
        if (details && details.reason) info.reason = details.reason.description || details.reason.code;
        info.deviceId = (ph.get_property && ph.get_property('$device_id')) || null;
        info.distinctId = ph.get_distinct_id ? ph.get_distinct_id() : null;
        if (!info.deviceId) {
          info.note = 'This browser has no PostHog device ID, so this device-bucketed flag returns false and control is shown.';
        } else if (info.flagValue === undefined || info.flagValue === null) {
          info.note = 'PostHog returned no value for this flag before the timeout, so control is shown.';
        }
      }
      console.info('[' + FLAG + ']', info);
    } catch (error) {}
  }

  function capture(event, properties) {
    if (IS_FORCED) return;
    try {
      if (window.posthog && typeof window.posthog.capture === 'function') {
        window.posthog.capture(event, properties);
      }
    } catch (error) {}
  }

  function create(tag, className) {
    var element = document.createElement(tag);
    if (className) element.className = className;
    return element;
  }

  function getThemeGallery() {
    var main = document.querySelector('.product__photos .product_main_photo_slider');
    if (!main) return null;
    var wrapper = main.querySelector('.swiper-wrapper');
    var photos = main.closest('.product__photos');
    var mount = photos && photos.parentElement;
    if (!wrapper || !photos || !mount) return null;
    var column = photos.closest('.product-single__sticky');
    var info = null;
    if (column && column.parentElement) {
      info = Array.prototype.filter.call(column.parentElement.children, function (element) {
        return element !== column && element.classList.contains('grid__item') && element.offsetHeight > 0;
      })[0] || null;
    }
    return { wrapper: wrapper, photos: photos, mount: mount, column: column, info: info };
  }

  function getThemeSlides(theme) {
    return Array.prototype.filter.call(theme.wrapper.children, function (element) {
      return element.classList.contains('swiper-slide');
    });
  }

  function readSlide(slide) {
    var image = slide.querySelector('.product-image-main img') || slide.querySelector('img');
    var template = image ? image.getAttribute('data-src') || '' : '';
    var src = '';
    var srcset = '';

    if (template.indexOf('{width}') !== -1) {
      src = template.replace('{width}', '1080');
      srcset = IMAGE_WIDTHS.map(function (width) {
        return template.replace('{width}', width) + ' ' + width + 'w';
      }).join(', ');
    } else if (image) {
      src = image.currentSrc || image.getAttribute('src') || template;
    }

    return {
      key: slide.getAttribute('data-mediaid') || src,
      framed: isFramedAlt(slide.getAttribute('data-mediaalt') || (image && image.getAttribute('alt')) || ''),
      src: src,
      srcset: srcset,
      ratio: parseFloat(image && image.getAttribute('data-aspectratio')) || 1,
      alt: image ? image.getAttribute('alt') || '' : '',
      zoomTarget: slide.querySelector('[data-fancybox]')
    };
  }

  // Same rule as the theme's getFrameFromAlt: the last "-" segment of the media alt names the frame.
  function isFramedAlt(alt) {
    var parts = String(alt || '').toLowerCase().split('-');
    var last = (parts[parts.length - 1] || '').replace(/[\s_()]/g, '');
    return FRAME_VALUES.indexOf(last) !== -1;
  }

  // Where the theme jumps after a frame change: to the swapped-in frame image, or to the first image otherwise.
  function frameJumpTarget(previous, items) {
    for (var i = 0; i < items.length; i += 1) {
      if (!previous[i] || previous[i].key !== items[i].key) return items[i].framed ? i : 0;
    }
    return null;
  }

  function clampRatio(ratio) {
    return Math.min(Math.max(ratio, 0.8), 1.91);
  }

  function buildImage(item, eager, sizes) {
    var image = create('img');
    if (item.srcset) {
      image.srcset = item.srcset;
      image.sizes = sizes;
    }
    image.src = item.src;
    image.alt = item.alt;
    image.decoding = 'async';
    image.loading = eager ? 'eager' : 'lazy';
    image.draggable = false;
    return image;
  }

  /*
   * Two-way sticky columns (desktop). A column taller than the viewport scrolls with the page until its
   * bottom reaches the bottom of the screen and then holds; scrolling back up, it moves with the page until
   * its top is visible and then holds there. A column that fits simply sticks below the header.
   *
   * position: sticky makes each column its own stacking context, which would trap the theme's fixed overlays
   * inside it (frame drawer, size chart, details modal) beneath the page backdrop. While one of those
   * overlays is open, its column drops back to static positioning so the overlay works as in control.
   */
  function StickyColumns(columns) {
    var self = this;
    this.columns = columns;
    this.tops = columns.map(function () { return null; });
    this.lastScrollY = window.scrollY;
    this.frame = null;
    this.onScroll = function () {
      if (self.frame) return;
      self.frame = window.requestAnimationFrame(function () {
        self.frame = null;
        self.update();
      });
    };
    this.onResize = function () { self.update(true); };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      columns.forEach(function (column) { self.resizeObserver.observe(column); });
    }
    columns.forEach(function (column) { column.classList.add('w99-041-sticky'); });
    this.watchOverlays();
    this.update(true);
  }

  StickyColumns.prototype.watchOverlays = function () {
    var self = this;
    this.overlays = this.columns.map(function (column) {
      return Array.prototype.filter.call(column.querySelectorAll('*'), function (element) {
        return window.getComputedStyle(element).position === 'fixed';
      });
    });
    if (!('MutationObserver' in window)) return;
    this.overlayTimer = null;
    this.overlayObserver = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (mutation) { return self.columns.indexOf(mutation.target) === -1; });
      if (!relevant) return;
      self.syncOverlays();
      // Closing overlays fade out; check again once the transition has finished.
      window.clearTimeout(self.overlayTimer);
      self.overlayTimer = window.setTimeout(function () { self.syncOverlays(); }, 450);
    });
    this.columns.forEach(function (column, position) {
      if (self.overlays[position].length) {
        self.overlayObserver.observe(column, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style'] });
      }
    });
    this.overlayObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    this.syncOverlays();
  };

  StickyColumns.prototype.syncOverlays = function () {
    var self = this;
    this.columns.forEach(function (column, position) {
      var open = self.overlays[position].some(function (element) {
        if (!element.isConnected || !element.getClientRects().length) return false;
        var style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') return false;
        return element.classList.contains('open') || parseFloat(style.opacity) > 0.01;
      });
      column.classList.toggle('w99-041-overlay-open', open);
    });
  };

  StickyColumns.prototype.topOffset = function () {
    var header = document.getElementById('SiteHeader');
    if (header && window.getComputedStyle(header).position === 'fixed') {
      return Math.max(0, Math.min(200, header.getBoundingClientRect().bottom)) + STICKY_GAP;
    }
    return STICKY_GAP;
  };

  StickyColumns.prototype.bottomOffset = function () {
    var bar = document.querySelector('.sticky-cart-wrapper.active');
    if (bar) {
      var rect = bar.getBoundingClientRect();
      if (rect.height > 0 && rect.top < window.innerHeight) return window.innerHeight - rect.top + STICKY_GAP;
    }
    return STICKY_GAP;
  };

  StickyColumns.prototype.update = function (reset) {
    var self = this;
    var scrollY = window.scrollY;
    var delta = reset ? 0 : scrollY - this.lastScrollY;
    var viewport = window.innerHeight;
    var topOffset = this.topOffset();
    var bottomOffset = this.bottomOffset();
    this.lastScrollY = scrollY;

    this.columns.forEach(function (column, position) {
      var lowest = viewport - bottomOffset - column.offsetHeight;
      var top;
      if (lowest >= topOffset) {
        top = topOffset;
      } else {
        var previous = self.tops[position] === null ? topOffset : self.tops[position];
        top = Math.min(topOffset, Math.max(lowest, previous - delta));
      }
      if (top !== self.tops[position]) {
        self.tops[position] = top;
        column.style.top = Math.round(top) + 'px';
      }
    });
  };

  StickyColumns.prototype.destroy = function () {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.overlayObserver) this.overlayObserver.disconnect();
    window.clearTimeout(this.overlayTimer);
    this.columns.forEach(function (column) {
      column.classList.remove('w99-041-sticky', 'w99-041-overlay-open');
      column.style.top = '';
    });
  };

  function Gallery(theme) {
    this.theme = theme;
    this.items = [];
    this.keys = '';
    this.index = 0;
    this.dotStart = 0;
    this.reportedIndex = 0;
    this.userInteracted = false;
    this.mode = '';
    this.sticky = null;
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);

    this.root = create('div', 'w99-041-gallery');
    this.root.setAttribute('data-w99-041-gallery', '');
    theme.mount.classList.add('w99-041-gallery-mount');
    theme.mount.insertBefore(this.root, theme.photos);
    if (theme.column) theme.column.classList.add('w99-041-gallery-column');
    if (theme.info) theme.info.classList.add('w99-041-info-column');

    var self = this;
    this.variantChangedAt = 0;
    document.addEventListener('variant:change', function () { self.variantChangedAt = Date.now(); });
    var onChange = function () { self.render(); };
    var resizeFrame = null;
    window.addEventListener('resize', function () {
      if (resizeFrame || self.mode !== 'desktop') return;
      resizeFrame = window.requestAnimationFrame(function () {
        resizeFrame = null;
        self.updateLayout();
        if (self.sticky) self.sticky.update(true);
      });
    });
    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', onChange);
    } else {
      this.mediaQuery.addListener(onChange);
    }
  }

  Gallery.prototype.sync = function () {
    var slides = getThemeSlides(this.theme);
    var keys = slides.map(function (slide) {
      return slide.getAttribute('data-mediaid') || '';
    }).join('|');
    if (keys === this.keys) return;

    var firstSync = this.keys === '';
    var previous = this.items;
    var items = slides.map(readSlide);
    this.keys = keys;
    this.setItems(items);

    // Follow the theme's frame logic only after a visitor changed the variant (never on page load).
    if (firstSync || Date.now() - this.variantChangedAt > VARIANT_CHANGE_WINDOW) return;
    var target = frameJumpTarget(previous, items);
    if (target === null) return;
    if (this.mode === 'mobile') {
      this.index = target;
      this.goTo(target, true);
      this.updateIndicators();
    } else if (target > 0) {
      this.revealTile(target);
    }
  };

  // Update the gallery to a new slide list: only changed images are swapped when the layout is unchanged.
  Gallery.prototype.setItems = function (items) {
    var previous = this.items;
    var mode = this.mediaQuery.matches ? 'mobile' : 'desktop';
    var containers = this.containers || [];
    this.items = items;
    if (this.index >= items.length) this.index = 0;

    if (mode !== this.mode || previous.length !== items.length || containers.length !== items.length) {
      this.render();
      return;
    }

    var self = this;
    items.forEach(function (item, position) {
      if (previous[position] && previous[position].key === item.key) return;
      self.swapImage(containers[position], item, position);
    });
  };

  Gallery.prototype.swapImage = function (container, item, position) {
    var current = container.querySelector('img');
    var sizes = this.mode === 'mobile' ? '100vw' : '(min-width: 769px) 50vw, 100vw';
    if (this.grid && this.grid.className.indexOf('w99-041-grid--count-') !== -1) {
      sizes = (this.items.length === 2 || position === 0) ? '(min-width: 769px) 75vw, 100vw' : sizes;
    }
    var image = buildImage(item, true, sizes);
    image.classList.add('w99-041-swap');
    if (current) container.replaceChild(image, current); else container.appendChild(image);
  };

  // Desktop: bring a tile into view only when it is completely off-screen (frame change while scrolled away).
  Gallery.prototype.revealTile = function (position) {
    var tile = this.containers && this.containers[position];
    if (!tile) return;
    var rect = tile.getBoundingClientRect();
    var header = document.getElementById('SiteHeader');
    var top = header && window.getComputedStyle(header).position === 'fixed' ? header.getBoundingClientRect().bottom : 0;
    if (rect.bottom > top && rect.top < window.innerHeight) return;
    window.scrollBy({ top: rect.top - top - STICKY_GAP, behavior: 'smooth' });
  };

  Gallery.prototype.observe = function () {
    var self = this;
    var timer = null;
    this.observer = new MutationObserver(function () {
      window.clearTimeout(timer);
      timer = window.setTimeout(function () { self.sync(); }, 60);
    });
    this.observer.observe(this.theme.wrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  };

  Gallery.prototype.render = function () {
    this.mode = this.mediaQuery.matches ? 'mobile' : 'desktop';
    this.root.setAttribute('data-mode', this.mode);
    this.root.textContent = '';
    this.track = null;
    this.strip = null;
    this.grid = null;
    this.containers = [];
    if (this.items.length) {
      if (this.mode === 'mobile') {
        this.renderMobile();
      } else {
        this.renderDesktop();
      }
    }
    this.updateLayout();
    this.updateSticky();
  };

  /*
   * Desktop: the product-info column keeps its theme width but moves to the right edge of the screen
   * (INFO_EDGE_GUTTER), and the gallery column fills the space from EDGE_GUTTER on the left up to it.
   * Negative margins keep both floats on one row. Screens no wider than the theme's page width change little.
   */
  Gallery.prototype.updateLayout = function () {
    var column = this.theme.column;
    var info = this.theme.info;
    if (!column) return;
    column.style.removeProperty('margin-left');
    column.style.removeProperty('width');
    if (info) info.style.removeProperty('margin-right');
    if (this.mode !== 'desktop') return;

    var columnRect = column.getBoundingClientRect();
    var paddingLeft = parseFloat(window.getComputedStyle(column).paddingLeft) || 0;
    var bleedLeft = Math.max(0, Math.floor(columnRect.left + paddingLeft - EDGE_GUTTER));
    var bleedRight = 0;
    if (info) {
      var infoRect = info.getBoundingClientRect();
      bleedRight = Math.max(0, Math.floor(document.documentElement.clientWidth - INFO_EDGE_GUTTER - infoRect.right));
    }

    if (bleedLeft || bleedRight) {
      column.style.setProperty('margin-left', (-bleedLeft) + 'px', 'important');
      column.style.setProperty('width', (columnRect.width + bleedLeft + bleedRight) + 'px', 'important');
      if (info && bleedRight) info.style.setProperty('margin-right', (-bleedRight) + 'px', 'important');
    }
  };

  Gallery.prototype.updateSticky = function () {
    var columns = [this.theme.column, this.theme.info].filter(Boolean);
    if (this.mode === 'desktop' && columns.length === 2) {
      if (!this.sticky) this.sticky = new StickyColumns(columns);
      else this.sticky.update(true);
    } else if (this.sticky) {
      this.sticky.destroy();
      this.sticky = null;
    }
  };

  Gallery.prototype.renderMobile = function () {
    var self = this;
    var total = this.items.length;
    var viewport = create('div', 'w99-041-viewport');
    var track = create('div', 'w99-041-track');
    var strip = create('div', 'w99-041-strip');

    viewport.style.setProperty('--w99-041-ratio', String(clampRatio(this.items[0].ratio)));
    track.setAttribute('role', 'region');
    track.setAttribute('aria-label', 'Product images');

    this.items.forEach(function (item, position) {
      var cell = create('button', 'w99-041-cell');
      cell.type = 'button';
      cell.setAttribute('aria-label', 'Zoom image ' + (position + 1) + ' of ' + total);
      cell.appendChild(buildImage(item, position < 2, '100vw'));
      cell.addEventListener('click', function () { self.openZoom(position); });
      strip.appendChild(cell);
    });

    track.appendChild(strip);
    viewport.appendChild(track);
    this.root.appendChild(viewport);

    this.track = track;
    this.strip = strip;
    this.cells = Array.prototype.slice.call(strip.children);
    this.containers = this.cells;
    this.counter = null;
    this.dots = [];
    this.dotsTrack = null;
    this.prevArrow = null;
    this.nextArrow = null;

    if (total > 1) {
      var counter = create('div', 'w99-041-counter');
      counter.setAttribute('aria-hidden', 'true');
      viewport.appendChild(counter);
      this.counter = counter;

      var dotsWindow = create('div', 'w99-041-dots');
      var dotsTrack = create('div', 'w99-041-dots-track');
      dotsWindow.setAttribute('aria-hidden', 'true');
      dotsWindow.style.width = (Math.min(total, DOTS_WINDOW) * DOT_STEP - (DOT_STEP - 6)) + 'px';
      for (var i = 0; i < total; i += 1) {
        var dot = create('span', 'w99-041-dot');
        dotsTrack.appendChild(dot);
        this.dots.push(dot);
      }
      dotsWindow.appendChild(dotsTrack);
      this.root.appendChild(dotsWindow);
      this.dotsTrack = dotsTrack;

      this.prevArrow = this.buildArrow('prev', 'Previous image', -1);
      this.nextArrow = this.buildArrow('next', 'Next image', 1);
      viewport.appendChild(this.prevArrow);
      viewport.appendChild(this.nextArrow);
    }

    var frame = null;
    var settle = null;
    track.addEventListener('scroll', function () {
      if (frame) return;
      frame = window.requestAnimationFrame(function () {
        frame = null;
        var position = Math.max(0, Math.min(total - 1, Math.round(track.scrollLeft / self.stride())));
        if (position !== self.index) {
          self.index = position;
          self.updateIndicators();
        }
      });
      window.clearTimeout(settle);
      settle = window.setTimeout(function () {
        if (self.userInteracted && self.index !== self.reportedIndex) {
          self.reportedIndex = self.index;
          capture('041_gallery_slide', { position: self.index + 1, total: total });
        }
      }, 250);
    }, { passive: true });

    var markInteraction = function () {
      self.userInteracted = true;
    };
    track.addEventListener('touchstart', markInteraction, { passive: true });
    track.addEventListener('pointerdown', markInteraction, { passive: true });

    this.goTo(this.index, false);
    this.updateIndicators(true);
  };

  Gallery.prototype.buildArrow = function (direction, label, step) {
    var self = this;
    var arrow = create('button', 'w99-041-arrow w99-041-arrow--' + direction);
    arrow.type = 'button';
    arrow.setAttribute('aria-label', label);
    arrow.innerHTML = CHEVRON;
    arrow.addEventListener('click', function (event) {
      event.preventDefault();
      self.userInteracted = true;
      var target = Math.max(0, Math.min(self.items.length - 1, self.index + step));
      if (target !== self.index) self.goTo(target, true);
    });
    return arrow;
  };

  Gallery.prototype.renderDesktop = function () {
    var self = this;
    var total = this.items.length;
    var grid = create('div', 'w99-041-grid');

    // Small sets keep the gallery as tall as the theme's single large image: two images stack full width,
    // three images show the first full width above a pair.
    if (total <= 3) grid.classList.add('w99-041-grid--count-' + total);
    grid.style.setProperty('--w99-041-ratio', String(clampRatio(this.items[0].ratio)));

    this.items.forEach(function (item, position) {
      var tile = create('button', 'w99-041-tile');
      tile.type = 'button';
      tile.setAttribute('aria-label', 'Zoom image ' + (position + 1) + ' of ' + total);
      var fullWidth = total === 2 || (total === 3 && position === 0);
      tile.appendChild(buildImage(item, position < 4, fullWidth ? '(min-width: 769px) 75vw, 100vw' : '(min-width: 769px) 50vw, 100vw'));
      tile.addEventListener('click', function () { self.openZoom(position); });
      grid.appendChild(tile);
    });

    this.root.appendChild(grid);
    this.grid = grid;
    this.containers = Array.prototype.slice.call(grid.children);
  };

  Gallery.prototype.stride = function () {
    if (this.cells && this.cells.length > 1) {
      return (this.cells[1].offsetLeft - this.cells[0].offsetLeft) || this.track.clientWidth || 1;
    }
    return (this.track && this.track.clientWidth) || 1;
  };

  Gallery.prototype.goTo = function (position, smooth) {
    if (!this.track) return;
    var left = position * this.stride();
    if (typeof this.track.scrollTo === 'function') {
      this.track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
    } else {
      this.track.scrollLeft = left;
    }
  };

  /*
   * Instagram-style dots: at most five are visible. The active dot travels along the row; the row slides
   * only when the active dot would reach an edge while more images remain, and the edge dot shrinks to
   * show that the set continues.
   */
  Gallery.prototype.updateIndicators = function (instant) {
    var total = this.items.length;
    if (this.counter) this.counter.textContent = (this.index + 1) + '/' + total;
    if (this.prevArrow) this.prevArrow.classList.toggle('is-hidden', this.index === 0);
    if (this.nextArrow) this.nextArrow.classList.toggle('is-hidden', this.index === total - 1);
    if (!this.dots.length) return;

    var start = 0;
    if (total > DOTS_WINDOW) {
      start = this.dotStart;
      if (this.index > start + DOTS_WINDOW - 2) start = this.index - (DOTS_WINDOW - 2);
      if (this.index < start + 1) start = this.index - 1;
      start = Math.max(0, Math.min(start, total - DOTS_WINDOW));
    }
    this.dotStart = start;
    var end = Math.min(total, start + DOTS_WINDOW) - 1;

    if (this.dotsTrack) {
      if (instant) this.dotsTrack.classList.add('is-instant');
      this.dotsTrack.style.transform = 'translateX(' + (-start * DOT_STEP) + 'px)';
      if (instant) {
        var dotsTrack = this.dotsTrack;
        window.requestAnimationFrame(function () {
          window.requestAnimationFrame(function () { dotsTrack.classList.remove('is-instant'); });
        });
      }
    }

    for (var i = 0; i < this.dots.length; i += 1) {
      var dot = this.dots[i];
      var outside = i < start || i > end;
      var edge = (i === start && start > 0) || (i === end && end < total - 1);
      dot.classList.toggle('is-active', i === this.index);
      dot.classList.toggle('is-edge', !outside && edge);
      dot.classList.toggle('is-outside', outside);
    }
  };

  Gallery.prototype.openZoom = function (position) {
    var item = this.items[position];
    if (!item) return;
    capture('041_gallery_zoom', { position: position + 1, total: this.items.length, device: this.mode });
    var target = item.zoomTarget;
    if (!target || !target.isConnected) return;
    if (window.jQuery) {
      window.jQuery(target).trigger('click');
    } else {
      target.click();
    }
  };

  // Wait until the theme gallery has initialised and applied its frame filter, so the first render is final.
  function whenThemeReady(callback) {
    var started = Date.now();
    (function check() {
      var main = document.querySelector('.product_main_photo_slider');
      if (main && main.classList.contains('swiper-initialized')) {
        window.requestAnimationFrame(function () { window.requestAnimationFrame(callback); });
      } else if (Date.now() - started > THEME_READY_TIMEOUT) {
        callback();
      } else {
        window.setTimeout(check, 50);
      }
    })();
  }

  function applyTest() {
    whenThemeReady(function () {
      var theme = getThemeGallery();
      if (!theme || theme.mount.querySelector('[data-w99-041-gallery]')) return;
      document.documentElement.classList.add(TEST_CLASS);
      var gallery = new Gallery(theme);
      gallery.sync();
      gallery.observe();
      window.w99041ProductGallery = gallery;
    });
  }

  function start() {
    var root = document.documentElement;
    if (root.hasAttribute('data-w99-041-started')) return;
    root.setAttribute('data-w99-041-started', '');
    if (typeof window.runExperiment !== 'function') return;

    window.runExperiment(FLAG, {
      debug: DEBUG,
      isEligible: function () {
        var theme = getThemeGallery();
        return !!theme && getThemeSlides(theme).length >= 2;
      },
      variants: {
        control: [],
        test: function () { applyTest(); }
      }
    }).then(logAssignment).catch(function (error) {
      console.warn(FLAG + ': defaulting to control', error);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
